import { StarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { createPortal } from "react-dom";

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    rating: number;
    comment: string;
    userName?: string;
    userEmail?: string;
  }) => void;
  userQuery: string;
  propertyName?: string;
  planName?: string;
  searchMode?: string;
  isSubmitting?: boolean;
  container?: HTMLElement;
}

const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userQuery,
  propertyName,
  planName,
  searchMode,
  isSubmitting = false,
  container,
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) return;
    onSubmit({
      rating,
      comment,
      userName: userName.trim() || undefined,
      userEmail: userEmail.trim() || undefined,
    });
    setSubmitted(true);
  };

  const handleClose = () => {
    setRating(0);
    setHoveredStar(0);
    setComment("");
    setUserName("");
    setUserEmail("");
    setSubmitted(false);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            Rate AI Response
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {submitted ? (
          /* Success state */
          <div className="px-6 py-12 text-center">
            <div className="text-4xl mb-4">&#10003;</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Thank you for your feedback!
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Your rating has been recorded and will help us improve the AI.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {/* Star Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How would you rate the quality of the AI result?
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    className="p-0.5 transition-transform hover:scale-110"
                  >
                    {star <= (hoveredStar || rating) ? (
                      <StarIconSolid className="h-8 w-8 text-yellow-400" />
                    ) : (
                      <StarIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </button>
                ))}
              </div>
              {rating === 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Please select a rating
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                If the result was incorrect, incomplete, or unhelpful, please
                explain what you expected the system to produce.
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-y"
                placeholder="Your comments..."
              />
            </div>

            {/* User Query (read-only context) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your prompt
              </label>
              <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 max-h-20 overflow-y-auto">
                {userQuery}
              </div>
            </div>

            {/* Property & Plan context */}
            {(propertyName || planName) && (
              <div className="flex gap-4">
                {propertyName && (
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Property
                    </label>
                    <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                      {propertyName}
                    </div>
                  </div>
                )}
                {planName && (
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Plan
                    </label>
                    <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                      {planName}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search scope */}
            {searchMode && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Search scope
                </label>
                <div className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md bg-gray-50 border border-gray-200 text-gray-700">
                  {searchMode === "global" ? "Global" : searchMode === "current" ? "Current Plan" : searchMode === "property" ? "Property" : searchMode}
                </div>
              </div>
            )}

            {/* Optional: Name & Email */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Your name"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Submit buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={rating === 0 || isSubmitting}
                className="px-6 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "Submitting..." : "Submit Rating"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    container || document.body
  );
};

export default RatingModal;
